<script>
import options from './PersonalFundraisingActiveCard.options'
export default options
</script>

<template>
  <article
    class="kb-personal-fundraising-active-card"
    :class="{ 'kb-personal-fundraising-active-card--memorial': isMemorial }"
  >
    <header class="kb-personal-fundraising-active-card__header">
      <div class="kb-personal-fundraising-active-card__title-row">
        <h3 class="kb-personal-fundraising-active-card__title">
          <a
            v-if="url"
            class="kb-personal-fundraising-active-card__title-link"
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="kb-personal-fundraising-active-card__title-text">{{ truncatedName }}</span>
            <span
              class="kb-personal-fundraising-active-card__title-arrow"
              aria-hidden="true"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                focusable="false"
              >
                <path
                  d="M0 8h11.75"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="m12.17 4 3.62 4-3.62 4"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="kb-visually-hidden"> (åbner i ny fane)</span>
          </a>
          <span
            v-else
            class="kb-personal-fundraising-active-card__title-text"
          >{{ truncatedName }}</span>
        </h3>
      </div>

      <div class="kb-personal-fundraising-active-card__actions">
        <a
          v-if="editUrl"
          class="kb-personal-fundraising-active-card__action kb-personal-fundraising-active-card__edit"
          :href="editUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span
            class="kb-personal-fundraising-active-card__edit-icon"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              focusable="false"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M20.317 2.25a1.5 1.5 0 0 1 2.049-.549l3.464 2a1.5 1.5 0 0 1 .55 2.05L13.13 28.697v.002l-.604 1.045-4.782 1.281-1.28-4.78L18.066 6.146l2.598 1.5-.5.866-1.732-1L8.799 24.2a1.995 1.995 0 0 1 1.96 1.605 1.995 1.995 0 0 1 2.37.895L25.513 5.25a.5.5 0 0 0-.183-.683l-3.464-2a.5.5 0 0 0-.683.183l-.75 1.3-.866-.5.75-1.3ZM12.264 28.2a1 1 0 0 0-1.733-1l-.866-.5a1 1 0 0 0-1.732-1l-.396.688.915 3.415 3.415-.915.396-.687v-.001Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span>{{ editLabel }}</span>
        </a>

        <a
          v-if="showUpdateTargetLink"
          class="kb-personal-fundraising-active-card__action"
          :href="fundraisingTargetUrl"
        >
          <span
            class="kb-personal-fundraising-active-card__action-icon kb-ui-icon kb-ui-icon--ui-dots-horizontal"
            aria-hidden="true"
          ></span>
          <span>{{ updateTargetLabel }}</span>
        </a>

        <div
          v-if="showMenu"
          class="kb-personal-fundraising-active-card__menu"
        >
          <PersonalFundraisingDashboardMenu
            :campaign-id="campaignId"
            :edit-url="editUrl"
            :qr-code-url="qrCodeUrl"
            :qr-code-image="qrCodeImage"
            :livestream-url="livestreamUrl"
            :is-memorial="isMemorial"
            :trigger-label="moreOptionsLabel"
            :update-target-label="updateTargetLabel"
            :qr-code-label="qrCodeLabel"
            :livestream-label="livestreamLabel"
          />
        </div>
      </div>
    </header>

    <div class="kb-personal-fundraising-active-card__body">
      <div
        v-if="isMemorial"
        class="kb-personal-fundraising-active-card__memorial-summary"
      >
        <p
          v-if="collectedAmountLabel"
          class="kb-personal-fundraising-active-card__collected-label"
        >
          {{ collectedAmountLabel }}
        </p>
        <p class="kb-personal-fundraising-active-card__collected-amount">
          {{ formattedAmount }}
        </p>
        <p
          v-if="donorCountParts"
          class="kb-personal-fundraising-active-card__donor-count"
        >
          {{ donorCountParts.before }}<span class="kb-personal-fundraising-active-card__donor-count-value">{{ donorCountParts.count }}</span>{{ donorCountParts.after }}
        </p>
      </div>

      <template v-else>
        <div
          v-if="hasTarget"
          class="kb-personal-fundraising-active-card__progress"
        >
          <div
            class="kb-personal-fundraising-active-card__progress-track"
            role="progressbar"
            :aria-valuenow="Math.round(progress)"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="formattedAmount"
          >
            <div
              class="kb-personal-fundraising-active-card__progress-fill"
              :class="{ 'kb-personal-fundraising-active-card__progress-fill--full': progress >= 100 }"
              :style="{ width: `${progress}%` }"
            ></div>
            <div
              class="kb-personal-fundraising-active-card__progress-marker"
              :class="{
                'kb-personal-fundraising-active-card__progress-marker--start':
                  progressMarkerEdge === 'start',
                'kb-personal-fundraising-active-card__progress-marker--end':
                  progressMarkerEdge === 'end'
              }"
              :style="{ left: `${progressMarkerPosition}%` }"
            >
              <div class="kb-personal-fundraising-active-card__progress-bubble">
                {{ formattedAmount }}
              </div>
              <span
                class="kb-personal-fundraising-active-card__progress-dot"
                aria-hidden="true"
              ></span>
            </div>
          </div>

          <div class="kb-personal-fundraising-active-card__progress-meta">
            <p class="kb-personal-fundraising-active-card__remaining">
              {{ remainingLabelParts.before }}<span class="kb-personal-fundraising-active-card__remaining-amount">{{ remainingLabelParts.amount }}</span>{{ remainingLabelParts.after }}
            </p>
            <p class="kb-personal-fundraising-active-card__target">
              {{ formattedTargetAmount }}
            </p>
          </div>
        </div>

        <div
          v-else
          class="kb-personal-fundraising-active-card__amount-only"
        >
          {{ formattedAmount }}
        </div>
      </template>

      <div class="kb-personal-fundraising-active-card__share">
        <button
          type="button"
          class="kb-button kb-button--full-color-spot kb-personal-fundraising-active-card__share-button"
          @click="openShareOverlay"
        >
          {{ shareCtaLabel }}
        </button>

        <p
          v-if="resolvedShareSubtext"
          class="kb-personal-fundraising-active-card__share-subtext"
        >
          {{ resolvedShareSubtext }}
        </p>
      </div>
    </div>
  </article>
</template>
